import Vue from 'vue'
import { Decrypt } from '@/utils/util'
import {
  LOCATION_NAME,
  LOCATION_PASSWORD,
  LOCATION_INPUTCODE,
  SSO_REDIRECT
} from '@/store/mutation-types'

/** 免登录场景固定验证码（与后端约定） */
export const SSO_CAPTCHA = '6363'

/**
 * 从 query 字符串中提取 username / password
 */
function parseSsoParamsFromQueryString(queryString) {
  if (!queryString || queryString.length <= 1) {
    return null
  }
  const params = new URLSearchParams(queryString.startsWith('?') ? queryString.slice(1) : queryString)
  const username = params.get('username')
  const password = params.get('password')
  if (!username || !password) {
    return null
  }
  return { username, password }
}

/**
 * 解析 SSO 参数，仅需 username、password；验证码免登时固定使用 SSO_CAPTCHA
 * 1. 标准格式：query 在 # 前  →  /?username=...&password=...#/login
 * 2. 兼容格式：query 在 # 后  →  /#/login?username=...&password=...（部分对接方误用）
 */
export function parseSsoParamsFromSearch() {
  const fromSearch = parseSsoParamsFromQueryString(window.location.search)
  if (fromSearch) {
    return { ...fromSearch, fromHash: false }
  }

  const hash = window.location.hash || ''
  const qIndex = hash.indexOf('?')
  if (qIndex !== -1) {
    const fromHash = parseSsoParamsFromQueryString(hash.slice(qIndex))
    if (fromHash) {
      return { ...fromHash, fromHash: true }
    }
  }

  return null
}

/**
 * 解密 URL / 路由 query 中的单个加密参数
 * 兼容：URL 编码、Vue Router 已解码、+ 被解析为空格
 */
function decryptParam(encrypted) {
  let val = String(encrypted)
  if (val.includes('%')) {
    val = decodeURIComponent(val)
  }
  if (val.includes(' ')) {
    val = val.replace(/ /g, '+')
  }
  return Decrypt(val)
}

function saveSsoCredentials(username, password) {
  Vue.ls.set(LOCATION_NAME, username)
  Vue.ls.set(LOCATION_PASSWORD, password)
  Vue.ls.set(LOCATION_INPUTCODE, SSO_CAPTCHA)
}

/**
 * 用 history.replaceState 清除地址栏 query，保留 hash 路由
 * @param {boolean} fromHash 参数是否位于 hash 内（#/login?xxx）
 */
export function clearQueryFromUrl(fromHash = false) {
  if (fromHash) {
    const hash = window.location.hash || ''
    const qIndex = hash.indexOf('?')
    const cleanHash = qIndex !== -1 ? hash.slice(0, qIndex) : hash
    window.history.replaceState(null, '', window.location.pathname + cleanHash)
    return
  }
  const cleanUrl = window.location.pathname + window.location.hash
  window.history.replaceState(null, '', cleanUrl)
}

/**
 * 解析 # 前的 SSO 参数 → AES 解密 → 写入本地缓存 → 清除地址栏 query
 * 应在路由守卫最早阶段调用
 * @returns {boolean} 是否成功处理 SSO 参数
 */
export function processSsoParams() {
  const raw = parseSsoParamsFromSearch()
  if (!raw) {
    return false
  }

  try {
    saveSsoCredentials(decryptParam(raw.username), decryptParam(raw.password))

    if (!raw.fromHash) {
      const hash = window.location.hash || ''
      const targetPath = hash.replace(/^#/, '').split('?')[0]
      if (targetPath && targetPath !== '/login' && targetPath !== '/') {
        Vue.ls.set(SSO_REDIRECT, targetPath)
      }
    }

    clearQueryFromUrl(raw.fromHash)
    return true
  } catch (e) {
    console.error('[SSO] 参数解密失败', e)
    clearQueryFromUrl()
    return false
  }
}

/** 是否存在待自动登录的 SSO 凭证 */
export function hasSsoCredentials() {
  return !!Vue.ls.get(LOCATION_NAME)
}

/** 读取 SSO 临时凭证 */
export function getSsoCredentials() {
  if (!hasSsoCredentials()) {
    return null
  }
  return {
    username: Vue.ls.get(LOCATION_NAME),
    password: Vue.ls.get(LOCATION_PASSWORD),
    inputCode: SSO_CAPTCHA
  }
}

/** 登录成功后清除 SSO 临时凭证 */
export function clearSsoCredentials() {
  Vue.ls.remove(LOCATION_NAME)
  Vue.ls.remove(LOCATION_PASSWORD)
  Vue.ls.remove(LOCATION_INPUTCODE)
  Vue.ls.remove(SSO_REDIRECT)
}

/** 获取 SSO 跳转目标路由（hash 中指定的路径） */
export function getSsoRedirect() {
  return Vue.ls.get(SSO_REDIRECT) || null
}

/**
 * 兼容链接无法改动：参数在 #/login? 后，由 Vue Router 解析到 route.query
 * 示例：/#/login?source=zt-reallin&username=xxx&password=yyy
 */
export function processSsoParamsFromRoute(route) {
  if (!route || !route.query) {
    return false
  }
  const username = route.query.username
  const password = route.query.password
  if (!username || !password) {
    return false
  }

  try {
    saveSsoCredentials(decryptParam(username), decryptParam(password))
    return true
  } catch (e) {
    console.error('[SSO] route.query 解密失败', e)
    return false
  }
}

/**
 * 汇总尝试：search → hash → route.query（登录页兜底）
 */
export function tryProcessSso(route) {
  if (processSsoParams()) {
    return true
  }
  return processSsoParamsFromRoute(route)
}
